import { find, reduce } from "lodash";
import { DetailedRound, ReducedSystem } from "../models/round";
import { Draw, Team } from "../models/fixture";
import { Bet, Coupon, Leg } from "../models/couponshares";

// Safe localStorage helpers. Accessing localStorage can throw on some mobile
// browsers (e.g. iOS Safari Private Mode, or when third-party iframe storage is
// partitioned/blocked). Swallow those errors so they don't crash the round view.
export const safeLocalStorageGet = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

export const safeLocalStorageSet = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        // ignore - storage unavailable
    }
};

export const safeLocalStorageRemove = (key: string): void => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        // ignore - storage unavailable
    }
};

export const couponTotal = (round: DetailedRound, coupon: Coupon, stakeMultiplier: number) => {

    let operatorFeePerRow = 0;
    if (round.operatorFeePerRow !== undefined && round.operatorFeePerRow !== null) {
        operatorFeePerRow = round.operatorFeePerRow.amount;
    }

    let costPerRow = Math.round(100 * (Number(coupon.stakePerRow.amount) + Number(operatorFeePerRow))) / 100;

    return reduce((coupon.bets || []).map(b =>
        betTotal(round.reducedSystems, b)), (x, y) => x + y, 0)
        * costPerRow * stakeMultiplier

}

export const betTotal = (reducedSystems: ReducedSystem[], bet: Bet) => {
    switch (bet.system.type) {
        case 'MATHEMATICAL':
            return reduce((bet.legs || []).map(l => Math.max(l.signs.length, 1)), (x, y) => x * y, 1);

        case 'REDUCED':
            const rs = find(reducedSystems, r => r.id === bet.system.reducedSystemId);

            if (!rs) {
                return 0;
            }

            return rs.nRows;

        default:
            return 0;
    }
}

export function getWholeAndHalfPutsFromArray(arr: Leg[]) {
    let wholePuts = 0;
    let halfPuts = 0;

    for (let i = 0; i < arr.length; i++) {
        const { signs } = arr[i];
        const { length } = signs || [];

        if (length === 3) {
            wholePuts += 1;
        } else if (length === 2) {
            halfPuts += 1;
        }
    }

    return {
        wholePuts,
        halfPuts
    };
}

const formatFraction = (v: number) => `${(v * 100).toFixed(0)}%`;

const signLabels: Record<string, string> = { ONE: "1", X: "X", TWO: "2" };

export const getDrawText = (draw: Draw, t: any, compact = false) => {

    const home = draw.outcomes?.find(d => d.sign === "ONE");
    const x = draw.outcomes?.find(d => d.sign === "X");
    const away = draw.outcomes?.find(d => d.sign === "TWO");
    const hasOutcomes = home !== undefined && x !== undefined && away !== undefined;

    if (draw.status === "FINISHED") {
        if (compact || !hasOutcomes) {
            return t('is_drawn');
        }
        const drawnSign = draw.sign ? signLabels[draw.sign] : undefined;
        return t('is_drawn') + ":" + (drawnSign ? " " + drawnSign : "") + " (1: " + formatFraction(home.probability) +
            " X: " + formatFraction(x.probability) + " 2: " + formatFraction(away.probability) + ")";
    }

    if (!hasOutcomes) {
        return t('is_draw');
    }

    return t('is_draw') + " " + formatFraction(home.probability) + " " +
        formatFraction(x.probability) + " " + formatFraction(away.probability)
}

export const getTeamName = (team: Team, t: any) => {

    if (!team.isNationalTeam) {
        return team.name;
    }
    return t('teams:' + team.name);
}
